import { useRef } from 'react'
import axios, { AxiosInstance } from 'axios'
import { useAuth0 } from '@auth0/auth0-react'

export function useAxiosAuth0(): AxiosInstance {
    const { getAccessTokenSilently } = useAuth0()
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'
    const API_V1_URL = `${API_BASE_URL}/v1`
    const axiosInstanceRef = useRef<AxiosInstance | null>(null)

    if (!axiosInstanceRef.current) {
        const instance = axios.create({
            baseURL: API_V1_URL,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        })

        instance.interceptors.request.use(
            async (config) => {
                try {
                    const token = await getAccessTokenSilently()
                    if (token && config.headers) {
                        config.headers.Authorization = `Bearer ${token}`
                    }
                    return config
                } catch (error) {
                    console.error('Error al obtener token de Auth0:', error)
                    return config
                }
            },
            (error) => Promise.reject(error)
        )

        instance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error?.response?.status === 401) {
                    // Only redirect when the JWT itself is invalid/expired.
                    // The `verifyAuth0Token` middleware returns this exact
                    // body when the token is rejected. Endpoint-specific
                    // 401s (handler-level auth/permission/state checks)
                    // must propagate as normal errors so the calling
                    // component can render a toast — the user should NOT
                    // get logged out because one endpoint refused them.
                    const body = error.response?.data
                    const isTokenFailure = body?.error === 'Auth0 Authentication failed'
                    if (isTokenFailure) {
                        console.warn('Token inválido o expirado, redirigiendo al login...')
                        window.location.replace('/login')
                    } else {
                        console.warn(
                            '[useAxiosAuth0] 401 from endpoint (not a token failure):',
                            error.config?.url,
                            body
                        )
                    }
                }
                return Promise.reject(error)
            }
        )

        axiosInstanceRef.current = instance
    }

    return axiosInstanceRef.current
}
